import { useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import isEqual from "../common/helpers/isEqual";
import { ShiftOrderContext } from "../features/orders/newOrder/short/OrderShortDataProvider";
import { AutomatchingStatus, OrderFields } from "../features/orders/orderTypes";
import useMessage from "./useMessage";
import usePrevious from "./usePrevious";

interface UseShortOrderResetProps {
  values: OrderFields;
  bundleRowId?: string;
  rowId: string;
  isFirstRow?: boolean;
}

export const useShortOrderReset = ({
  values,
  bundleRowId,
  rowId,
  isFirstRow,
}: UseShortOrderResetProps) => {
  const { t } = useTranslation("order");
  const { sendMessage } = useMessage();
  const previousValues = usePrevious(values);

  const { bundling, handleChange, handleError, isAdditionalInfoChanged } =
    useContext(ShiftOrderContext);

  const resetManualOffering = useCallback(() => {
    const resetAutomatching = {
      automatchingStatus: AutomatchingStatus.On,
      automatchingStartDelayMinutes: null,
      talents: [],
      talentsFromWorkLocationPool: false,
      pool: null,
    };
    handleChange(
      "automatching",
      resetAutomatching,
      rowId,
      values.id,
      isFirstRow,
    );
    sendMessage(t("order:addManuallyOfferedTalentsAgain"), "warning", {
      timeOut: 5000,
    });
  }, [handleChange, rowId, values.id, isFirstRow, sendMessage, t]);

  const resetFlexibleStart = useCallback(() => {
    if (values.flexibleStartUntil) {
      handleChange("flexibleStartUntil", "", rowId, values.id, isFirstRow);
      handleError(false, {
        id: values.id,
        field: "flexibleStartUntil",
        errorType: "any",
      });
      sendMessage(t("flexibleStartResetInBundle"), "warning", {
        timeOut: 5000,
      });
    }
  }, [
    values.flexibleStartUntil,
    handleChange,
    handleError,
    rowId,
    values.id,
    isFirstRow,
    sendMessage,
    t,
  ]);

  const resetTimeBasedNotification = useCallback(() => {
    if (values.notifyMinutesBeforeIfNotFulfilled) {
      // Always use isFirstRow=true for bundled rows to ensure reset applies to all rows
      const shouldResetAsFirstRow = bundleRowId ? true : isFirstRow;
      handleChange(
        "notifyMinutesBeforeIfNotFulfilled",
        0,
        rowId,
        values.id,
        shouldResetAsFirstRow,
      );
      sendMessage(t("timeBasedNotificationReset"), "warning", {
        timeOut: 5000,
      });
    }
  }, [
    values.notifyMinutesBeforeIfNotFulfilled,
    handleChange,
    rowId,
    values.id,
    isFirstRow,
    bundleRowId,
    sendMessage,
    t,
  ]);

  useEffect(() => {
    if (previousValues) {
      const dateChanged = values.date !== previousValues.date;
      const timeChanged = values.time !== previousValues.time;

      if (dateChanged || timeChanged) {
        if (!isAdditionalInfoChanged) {
          // reset notifyMinutesBeforeIfNotFulfilled if date or time changed
          resetTimeBasedNotification();
        }
      }

      const workLocationChanged =
        values.workLocation.id !== previousValues.workLocation.id;
      const orderTemplateChanged = !isEqual(
        values.orderTemplate,
        previousValues.orderTemplate,
      );
      const amountChanged = values.amount !== previousValues.amount;

      if (!!values.automatching.talents.length) {
        if (
          workLocationChanged ||
          orderTemplateChanged ||
          dateChanged ||
          timeChanged ||
          amountChanged
        ) {
          // reset manually offered talents if the user changes the order details/requirements
          resetManualOffering();
        }
      }
    }
  }, [
    previousValues,
    values.date,
    values.orderTemplate,
    values.time,
    values.workLocation.id,
    values.automatching.talents.length,
    values.amount,
    values.notifyMinutesBeforeIfNotFulfilled,
    isAdditionalInfoChanged,
    resetManualOffering,
    resetTimeBasedNotification,
  ]);

  // Reset manually offered talents when added to bundle
  useEffect(() => {
    if (bundling.resetManuallyAddedTalents) {
      resetManualOffering();
    }
  }, [bundling.resetManuallyAddedTalents, resetManualOffering]);

  // Reset flexible start when added to bundle
  useEffect(() => {
    if (bundleRowId && values.flexibleStartUntil) {
      resetFlexibleStart();
    }
  }, [bundleRowId, values.flexibleStartUntil, resetFlexibleStart]);

  // Reset time-based notification when added to bundle
  useEffect(() => {
    if (bundling.resetTimeBasedNotification) {
      resetTimeBasedNotification();
    }
  }, [bundling.resetTimeBasedNotification, resetTimeBasedNotification]);
};
